import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { FileInput, Input, Radio, Select } from 'common-src/components/base';
import { apiRequest, getRestErrorMessage, HttpMethods } from 'common-src/features/rest';
import { DocumentCategoryOptions, DocumentStatus } from 'common-src/models/PatientDocument';
import {
  DocumentButtonLabel,
  DocumentCategory,
  DocumentUploadType,
} from 'common-src/models/PatientDocument/constants';

import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import styles from './UploadDocumentPopup.module.scss';

const UploadDocumentPopup = ({ patientId, onClose, defaultCategory = null }) => {
  const [data, setData] = useState({
    fileCategory: defaultCategory ? { value: defaultCategory, label: defaultCategory } : null,
    file: null,
  });
  const [uploadType, setUploadType] = useState(DocumentUploadType.PDF);
  const [linkUrl, setLinkUrl] = useState('');

  const { errors, setErrors, renderButtons } = usePopup();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file is PDF
    if (file.type !== 'application/pdf') {
      setErrors({ file: 'Please select a PDF file' });
      return;
    }

    setData({ ...data, file });
    setErrors({ file: file.size > 10 * 1024 * 1024 ? 'File size must be less than 10MB' : '' });
  };

  const isValidUrl = (value) => {
    try {
      return Boolean(new URL(value));
    } catch {
      return false;
    }
  };

  const handleLinkChange = (value) => {
    setLinkUrl(value);
    if (isValidUrl(value)) {
      setErrors((prev) => ({ ...prev, linkUrl: '' }));
    } else {
      setErrors((prev) => ({ ...prev, linkUrl: 'Please enter a valid URL' }));
    }
  };

  /**
   * Handles the document upload process with a multi-stage workflow.
   *
   * This function manages the complete document upload process including:
   * 1. Creating a document record in the database
   * 2. Obtaining a presigned URL for S3 upload
   * 3. Uploading the file directly to S3 storage
   * 4. Updating the document status after upload
   *
   * @param {Function} loadingCallback - Callback to indicate loading state has started
   * @param {Function} successCallback - Callback to execute on successful upload with a success message
   * @param {Function} errorCallback - Callback to execute when an error occurs with an error message
   * @returns {Promise<void>} - A promise that resolves when the upload process is complete
   * @throws Will call errorCallback with appropriate error message but won't throw exceptions outside
   */
  const handleSubmit = async (loadingCallback, successCallback, errorCallback) => {
    try {
      loadingCallback();

      if (
        data.fileCategory?.value === DocumentCategory.SharedEducationalResources &&
        uploadType === DocumentUploadType.LINK
      ) {
        if (!isValidUrl(linkUrl)) {
          errorCallback('Please enter a valid URL');
          return;
        }

        const response = await apiRequest({
          endpoint: 'patientDocuments',
          method: HttpMethods.Post,
          body: {
            patient_id: patientId,
            fileName: linkUrl,
            fileType: 'application/link',
            fileCategory: data.fileCategory.value,
            size: 0,
            link_url: linkUrl,
          },
        });

        if (!response.ok) {
          const err = await response.json();
          const errMsg = getRestErrorMessage(err) || '';
          errorCallback(errMsg);
          return;
        }

        successCallback('Web link uploaded successfully!');
        onClose(true);
        return;
      }

      const response = await apiRequest({
        endpoint: 'patientDocuments',
        method: HttpMethods.Post,
        body: {
          patient_id: patientId,
          fileName: data.file.name,
          fileType: data.file.type,
          fileCategory: data.fileCategory.value,
          size: data.file.size,
        },
      });

      if (!response.ok) {
        const err = await response.json();
        const errMsg = getRestErrorMessage(err) || '';
        errorCallback(errMsg);
        return;
      }

      const responseData = await response.json();

      if (!responseData.id || !responseData?.presignedPost) {
        errorCallback('Failed to get presigned URL');
        return;
      }

      // Upload directly to S3 using the presigned URL
      const uploadResponse = await fetch(responseData.presignedPost, {
        method: 'PUT',
        body: data.file,
        headers: {
          'Content-Type': data.file.type,
        },
      });

      if (!uploadResponse.ok) {
        // Update document status to failed if upload fails
        await apiRequest({
          endpoint: `patientDocuments/${responseData.id}/status`,
          method: HttpMethods.Patch,
          body: { status: DocumentStatus.FailedToUpload },
        });

        errorCallback('Failed to upload file to storage');
        return;
      }

      // Update document status to uploaded
      const statusUpdateResponse = await apiRequest({
        endpoint: `patientDocuments/${responseData.id}/status`,
        method: HttpMethods.Patch,
        body: { status: DocumentStatus.Uploaded },
      });

      if (!statusUpdateResponse.ok) {
        errorCallback('File uploaded but failed to update status');
        return;
      }

      successCallback('Document uploaded successfully!');
      onClose(true);
    } catch (error) {
      errorCallback('An error occurred during file upload');
    }
  };

  return (
    <BasePopup
      id="upload-document"
      open
      onClose={onClose}
      title={defaultCategory ? `Upload ${defaultCategory}` : 'Upload Document'}
      customStyle={
        defaultCategory
          ? { minWidth: 0, width: 'calc(100vw - 32px)', maxWidth: '700px', boxSizing: 'border-box' }
          : undefined
      }
    >
      <div className={styles.content}>
        {!defaultCategory && (
          <Select
            id="file-category"
            classNames={['m-b-20']}
            label="File category"
            options={DocumentCategoryOptions}
            onChange={(op) => {
              setData({ ...data, fileCategory: op });
              setUploadType(DocumentUploadType.PDF);
              setLinkUrl('');
              setErrors({});
            }}
            value={data.fileCategory}
            required
            placeholder="Select document category"
            size="small"
          />
        )}
        {data.fileCategory?.value === DocumentCategory.SharedEducationalResources && (
          <div
            className={styles.uploadTypeToggle}
            style={{ marginBottom: 16, display: 'flex', gap: 16 }}
          >
            <Radio
              id="upload-type-pdf"
              label="PDF"
              checked={uploadType === DocumentUploadType.PDF}
              onChange={() => setUploadType(DocumentUploadType.PDF)}
            />
            <Radio
              id="upload-type-link"
              label="Web Link"
              checked={uploadType === DocumentUploadType.LINK}
              onChange={() => setUploadType(DocumentUploadType.LINK)}
            />
          </div>
        )}

        {uploadType === DocumentUploadType.PDF && (
          <FileInput
            id="document-file"
            inputAccept=".pdf"
            placeholder="Click to select PDF file"
            description="PDF files only, max 10MB"
            value={data.file}
            errorText={errors.file}
            handleFileChange={handleFileChange}
          />
        )}
        {uploadType === DocumentUploadType.LINK && (
          <Input
            id="web-link"
            label="Web Link"
            value={linkUrl}
            onTextChange={handleLinkChange}
            placeholder="https://example.com"
            errorText={errors.linkUrl}
            required
          />
        )}
      </div>
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        onClose,
        onSubmit: handleSubmit,
        isSubmitEnabled:
          data.fileCategory &&
          ((uploadType === DocumentUploadType.PDF && data.file && !errors.file) ||
            (uploadType === DocumentUploadType.LINK && linkUrl && !errors.linkUrl)),
        submitButtonText:
          uploadType === DocumentUploadType.LINK
            ? DocumentButtonLabel.LINK
            : DocumentButtonLabel.PDF,
      })}
    </BasePopup>
  );
};

UploadDocumentPopup.propTypes = {
  patientId: PropTypes.number,
  onClose: PropTypes.func,
  defaultCategory: PropTypes.string,
};

export default UploadDocumentPopup;
